import api from "./axios";

export interface Material {
    id: string;
    class_subject_id: string;
    teacher_id: string;
    title: string;
    description?: string;
    type: string;
    file_path?: string;
    file_name?: string;
    file_size?: number;
    mime_type?: string;
    url?: string;
    is_published: boolean;
    published_at?: string;
    created_at: string;
    updated_at: string;

    // Relationships
    class_subject?: {
        id: string;
        class?: {
            id: string;
            grade_level: string;
            name: string;
        };
        subject?: {
            id: string;
            name: string;
            code: string;
        };
        teacher?: {
            id: string;
            user?: {
                id: string;
                name: string;
                email: string;
            };
        };
    };

    teacher?: {
        id: string;
        nip: string;
        user?: {
            id: string;
            name: string;
            email: string;
        };
    };

    // Additional computed fields
    file_url?: string;
    file_size_formatted?: string;
    type_display?: string;
}

export interface CreateMaterialData {
    class_subject_id: string;
    teacher_id: string;
    title: string;
    description?: string;
    type: string;
    url?: string;
    is_published?: boolean;
    file?: File;
}

export interface UpdateMaterialData {
    class_subject_id?: string;
    teacher_id?: string;
    title?: string;
    description?: string;
    type?: string;
    url?: string;
    is_published?: boolean;
    file?: File;
}

export interface MaterialFilters {
    search?: string;
    class_subject_id?: string;
    class_id?: string;
    teacher_id?: string;
    type?: string;
    is_published?: boolean;
    page?: number;
    per_page?: number;
}

export interface MaterialListResponse {
    success: boolean;
    message: string;
    data: {
        items: Material[];
        meta: {
            current_page: number;
            last_page: number;
            per_page: number;
            total: number;
            from: number;
            to: number;
        };
    };
}

export interface MaterialResponse {
    success: boolean;
    message: string;
    data: Material;
}

export const materialApi = {
    async getAll(filters: MaterialFilters = {}): Promise<MaterialListResponse> {
        const response = await api.get("/v1/materials", { params: filters });
        return response.data;
    },

    async getById(id: string): Promise<MaterialResponse> {
        const response = await api.get(`/v1/materials/${id}`);
        return response.data;
    },

    async create(data: CreateMaterialData): Promise<MaterialResponse> {
        const formData = new FormData();

        Object.keys(data).forEach((key) => {
            if (key === "file" && data.file instanceof File) {
                formData.append("file", data.file);
            } else if (data[key as keyof CreateMaterialData] !== undefined) {
                formData.append(
                    key,
                    String(data[key as keyof CreateMaterialData]),
                );
            }
        });

        const response = await api.post("/v1/materials", formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        return response.data;
    },

    async update(
        id: string,
        data: UpdateMaterialData,
    ): Promise<MaterialResponse> {
        const formData = new FormData();

        Object.keys(data).forEach((key) => {
            if (key === "file" && data.file instanceof File) {
                formData.append("file", data.file);
            } else if (data[key as keyof UpdateMaterialData] !== undefined) {
                formData.append(
                    key,
                    String(data[key as keyof UpdateMaterialData]),
                );
            }
        });

        const response = await api.post(
            `/v1/materials/${id}?_method=PUT`,
            formData,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        );
        return response.data.data;
    },

    async delete(id: string): Promise<void> {
        await api.delete(`/v1/materials/${id}`);
    },

    async publish(id: string): Promise<Material> {
        const response = await api.post(`/v1/materials/${id}/publish`);
        return response.data.data;
    },

    async unpublish(id: string): Promise<Material> {
        const response = await api.post(`/v1/materials/${id}/unpublish`);
        return response.data.data;
    },

    async getByClassSubject(classSubjectId: string): Promise<Material[]> {
        const response = await api.get(
            `/v1/materials/class-subject/${classSubjectId}`,
        );
        return response.data.data || [];
    },

    async getByTeacher(teacherId: string): Promise<Material[]> {
        const response = await api.get(`/v1/materials/teacher/${teacherId}`);
        return response.data.data || [];
    },

    async getByClass(classId: string): Promise<Material[]> {
        const response = await api.get(`/v1/materials/class/${classId}`);
        return response.data.data || [];
    },

    async getByType(type: string): Promise<Material[]> {
        const response = await api.get(`/v1/materials/type/${type}`);
        return response.data.data || [];
    },

    async getStudentMaterials(): Promise<{
        current_class: {
            id: string;
            name: string;
            grade_level: string;
        } | null;
        materials: Material[];
    }> {
        const response = await api.get("/v1/student/materials");
        return response.data.data || { current_class: null, materials: [] };
    },

    async download(id: string): Promise<Blob> {
        const response = await api.get(`/v1/materials/${id}/download`, {
            responseType: "blob",
        });
        return response.data;
    },
};

export default materialApi;
